import React, { useMemo, useCallback, forwardRef } from 'react';
import Draggable from 'react-draggable';
import { ResizableBox } from 'react-resizable';
import Window from './Window';
import { useWindowData } from './includes/WindowContext';

const WindowBorder = React.memo(({
	windowId,
	title,
	onMinimize,
	onMaximize,
	onClose,
	onHelp,
	onClick,
	onContextMenu,
	minimised,
	maximised,
	initialPosition,
	zIndex,
	appData,
	icon,
	isActiveWindow,
	children,
	embedable,
	inContainer,
	metadata,
	windowA
}) => {

	const { getWindowContent } = useWindowData();
	const content = getWindowContent(windowId);
	const { license, embedMode } = appData;

	const windowIcon = icon || content?.icon || '🖼️';
		

	const MyHandle = useMemo(() => forwardRef((props, ref) => {
		const { handleAxis, ...restProps } = props;
		return <div ref={ref} className={`handle handle-${handleAxis}`} {...restProps} />;
	}), []);

	const handleStart = useCallback((e, data) => {
		console.log('handleStart');
		e.stopPropagation();
	}, []);

	if(embedMode && inContainer) {
		if(isActiveWindow) {
			maximised = true;
		}else{
			minimised = true;
		}

	}

	// IMPORTANT: when minimised we hide via CSS instead of returning
	// `null`. Returning null unmounts the entire wizard subtree, which
	// blew away any non-`useIsolatedState` state (text fields, scan
	// progress, frost ceremony status, etc) the moment the user
	// minimised it. Hiding via `display:none` keeps the React tree
	// alive so a subsequent restore picks up exactly where the user
	// left off — no redraw side-effects.
	const minimisedStyle = minimised
		? { display: 'none' }
		: null;

	if (maximised) {
		return (
			<div
				className='maximised'
				style={{
					zIndex: zIndex,
					display: 'flex',
					position: 'absolute',
					top: 0,
					left: 0,
					width: '100%',
					height: '100%',
					...(minimisedStyle || {}),
				}}
			>
				<div className="window-border" style={{ flexGrow: 1 }}>
					<Window
						title={title}
						onMinimize={onMinimize}
						onMaximize={onMaximize}
						onClose={onClose}
						onHelp={onHelp}
						onContextMenu={onContextMenu}
						maximised={maximised}
						onClick={onClick}
						appData={appData}
						icon={windowIcon}
						isActiveWindow={isActiveWindow}
						embedable={embedable}
						embeded={embedMode && inContainer}
						metadata={metadata}
						windowA={windowA}
					>
						{children}
					</Window>
				</div>
			</div>
		);
	}

	if (!initialPosition || !initialPosition.width || !initialPosition.height) {
		initialPosition = {
			x: (window.innerWidth < 600) ? 0 : (5 + zIndex * 5),
			y: (window.innerWidth < 600) ? 0 : (5 + zIndex * 5),
			width: 350,
			height: 400
		};
	}

	if (initialPosition.x === 'auto') {
		// Only VISIBLE windows may anchor the cascade. Minimised/hidden
		// window-borders measure 0×0 at whatever offset their container
		// happens to sit at — anchoring on one used to compute a negative
		// y on restore-from-maximised (hash-opened purse), shoving the
		// titlebar off the top of the screen.
		const visible = Array.from(document.querySelectorAll('.window-border')).filter((el) => {
			const r = el.getBoundingClientRect();
			return r.width > 0 && r.height > 0;
		});
		let count = 0;
		visible.forEach((el) => {
			if (el.querySelector('.title-text')?.textContent === title) {
				count++;
			}
		});

		const lastWindow = visible[visible.length - 1];
		if (count === 0 || !lastWindow) {
			initialPosition.x = 0;
			initialPosition.y = 0;
		} else {
			const lastWindowRect = lastWindow.getBoundingClientRect();
			const windowRect = lastWindow.parentElement.getBoundingClientRect();
			if (lastWindowRect.right + initialPosition.width < window.innerWidth) {
				initialPosition.x = lastWindowRect.right;
				initialPosition.y = lastWindowRect.top - windowRect.top;
			} else {
				initialPosition.x = 0;
				initialPosition.y = lastWindowRect.top + 50;
			}
		}

		// Belt and braces: whatever the anchor maths said, the titlebar
		// must stay reachable — clamp into the viewport.
		initialPosition.x = Math.max(0, Math.min(initialPosition.x, window.innerWidth - 80));
		initialPosition.y = Math.max(0, Math.min(initialPosition.y, window.innerHeight - 60));
	}

	return (
		<Draggable
			handle={"div>span>div>div.title>div.title-text"}
			defaultPosition={{ x: initialPosition.x, y: initialPosition.y }}
			bounds="parent"
			onStart={handleStart}
			enableUserSelectHack={false}
			cancel='.search-text-box'
		>
			<div
				className={'window-border' + (license ? ' win95' : '')}
				style={{
					zIndex: zIndex,
					width: 'fit-content',
					...(minimisedStyle || {}),
				}}
			>
				<ResizableBox
					width={initialPosition.width}
					height={initialPosition.height}
					axis='both'
					minConstraints={[200, 200]}
					resizeHandles={['se', 'ne', 'nw', 'sw']}
					handle={<MyHandle />}
				>
					<span style={{ width: '100%', height: '100%' }} onMouseDown={onClick}>
						<Window
							title={title}
							onMinimize={onMinimize}
							onMaximize={onMaximize}
							onClose={onClose}
							onHelp={onHelp}
							onContextMenu={onContextMenu}
							maximised={maximised}
							icon={windowIcon}
							appData={appData}
							isActiveWindow={isActiveWindow}
							embedable={embedable}
							embeded={embedMode && inContainer}
							metadata={metadata}
							windowA={windowA}
						>
							{children}
						</Window>

						<div className="bottomright handle react-resizable-handle-se"></div>
						<div className="topright handle react-resizable-handle-ne"></div>
						<div className="topleft handle react-resizable-handle-nw"></div>
						<div className="bottomleft handle react-resizable-handle-sw"></div>
					</span>
				</ResizableBox>
			</div>
		</Draggable>
	);
});

export default WindowBorder;
